import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  useLoadScript,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "600px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const toNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (obj) => {
  if (!obj) return null;
  const lat = toNumber(obj.lat ?? obj.latitude ?? obj.origin_lat ?? obj.pickup_lat ?? obj.destination_lat);
  const lng = toNumber(obj.lng ?? obj.longitude ?? obj.origin_lng ?? obj.pickup_lng ?? obj.destination_lng);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const RideShareDriverNegotiationRoutePreview = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const mapRef = useRef(null);

  const [origin, setOrigin] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState("");

  const center = useMemo(() => origin || pickup || destination || defaultCenter, [origin, pickup, destination]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const originLat = toNumber(params.get("origin_lat"));
    const originLng = toNumber(params.get("origin_lng"));
    const pickupLat = toNumber(params.get("pickup_lat"));
    const pickupLng = toNumber(params.get("pickup_lng"));
    const destinationLat = toNumber(params.get("destination_lat"));
    const destinationLng = toNumber(params.get("destination_lng"));

    if (originLat != null && originLng != null) setOrigin({ lat: originLat, lng: originLng });
    if (pickupLat != null && pickupLng != null) setPickup({ lat: pickupLat, lng: pickupLng });
    if (destinationLat != null && destinationLng != null) setDestination({ lat: destinationLat, lng: destinationLng });
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const raw = event?.data;
        if (!raw) return;
        const payload = typeof raw === "string" ? JSON.parse(raw) : raw;

        const originCandidate = payload?.origin || payload?.trip || payload?.locations?.origin;
        const pickupCandidate = payload?.pickup || payload?.request || payload?.locations?.pickup;
        const destinationCandidate = payload?.destination || payload?.trip || payload?.locations?.destination;

        const o = payload?.trip
          ? {
              lat: payload.trip?.origin_lat ?? payload.trip?.origin?.lat,
              lng: payload.trip?.origin_lng ?? payload.trip?.origin?.lng,
            }
          : originCandidate;

        const p = payload?.request
          ? {
              lat: payload.request?.pickup_lat ?? payload.request?.pickup?.lat,
              lng: payload.request?.pickup_lng ?? payload.request?.pickup?.lng,
            }
          : pickupCandidate;

        const d = payload?.trip
          ? {
              lat: payload.trip?.destination_lat ?? payload.trip?.destination?.lat,
              lng: payload.trip?.destination_lng ?? payload.trip?.destination?.lng,
            }
          : destinationCandidate;

        const nextOrigin = toLatLng(o);
        const nextPickup = toLatLng(p);
        const nextDestination = toLatLng(d);

        if (nextOrigin) setOrigin(nextOrigin);
        if (nextPickup) setPickup(nextPickup);
        if (nextDestination) setDestination(nextDestination);
      } catch (e) {
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!origin || !destination) return;

    const service = new window.google.maps.DirectionsService();

    const waypoints = pickup ? [{ location: pickup, stopover: true }] : [];

    service.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
          const totalSeconds = result.routes[0].legs.reduce((sum, leg) => sum + leg.duration.value, 0);
          setEta(`${Math.ceil(totalSeconds / 60)} min`);
        } else {
          setDirections(null);
          setEta("");
        }
      }
    );
  }, [isLoaded, origin, pickup, destination]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <h2>Driver Negotiation Route Preview</h2>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={center}
        onLoad={(map) => (mapRef.current = map)}
      >
        {origin && <Marker position={origin} title="Your Origin" label="O" />}
        {pickup && <Marker position={pickup} title="Passenger Pickup" label="P" />}
        {destination && <Marker position={destination} title="Destination" label="D" />}

        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#800080",
                strokeWeight: 5,
                strokeOpacity: 0.85,
              },
              preserveViewport: false,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>ETA:</strong> {eta || "N/A"}
        </p>
      </div>
    </div>
  );
};

export default RideShareDriverNegotiationRoutePreview;
